import { Injectable, Signal, computed } from '@angular/core';
import { IFrameReduction, ILoanProfile, ILoanProfileOption, ISimulatorData, ISimulatorModel } from '@partners/api';
import { InterestTypeEnum } from '@partners/constants';
import { IRemoteError } from '@partners/errors';
import { Observable } from 'rxjs';
import { StateAccessService } from '../../../services';
import { ISimulatorState, SimulatorActions, SimulatorSelectors } from '../store';

@Injectable({
  providedIn: 'root'
})
export class SimulatorStore extends StateAccessService<ISimulatorState> {
  readonly state$: Observable<ISimulatorState>;

  readonly data$$: Signal<ISimulatorData>;
  readonly model$$: Signal<ISimulatorModel>;
  readonly error$$: Signal<IRemoteError>;
  readonly loanProfile$$: Signal<ILoanProfile>;
  readonly loanProfileOptions$$: Signal<ILoanProfileOption[]>;
  readonly frameReduction$$: Signal<IFrameReduction>;
  readonly currentOption$$: Signal<ILoanProfileOption>;
  readonly defaultProfileOption$$: Signal<ILoanProfileOption>;
  readonly isVariableInterest$$: Signal<boolean>;

  private readonly _allLoanProfiles$$: Signal<ILoanProfile[]>;

  constructor() {
    super();

    this.model$$ = this.stateFacade.selectSignal$(SimulatorSelectors.model);
    this.data$$ = this.stateFacade.selectSignal$(SimulatorSelectors.data);
    this.error$$ = this.stateFacade.selectSignal$(SimulatorSelectors.error);
    this.currentOption$$ = this.stateFacade.selectSignal$(SimulatorSelectors.option);

    this._allLoanProfiles$$ = this.stateFacade.selectSignal$(SimulatorSelectors.loanProfiles);

    this.loanProfileOptions$$ = computed(() => this.loanProfile$$().loanProposalOptions);
    this.frameReduction$$ = computed(() => this.data$$().frameReduction);
    this.defaultProfileOption$$ = computed(() => {
      const options = this.loanProfileOptions$$();
      return options?.length ? options[0] : undefined;
    });
    this.loanProfile$$ = computed(() => {
      const profiles = this._allLoanProfiles$$();
      if (profiles?.length > 0) {
        return profiles[0];
      }
      return undefined;
    });
    this.isVariableInterest$$ = computed(() => {
      const profile = this.loanProfile$$();
      if (profile) {
        return profile.interestType === InterestTypeEnum.variable;
      }
      return false;
    });
  }

  dispatchSimulatorReset() {
    this.stateFacade.dispatch(SimulatorActions.reset());
  }

  dispatchUnSelectProfileOption() {
    this.stateFacade.dispatch(SimulatorActions.unSelectProfileOption());
  }

  dispatchSelectProfileOption(profileOption: ILoanProfileOption = this.currentOption$$()) {
    this.stateFacade.dispatch(SimulatorActions.selectProfileOption({ profileOption }));
  }

  dispatchSimulatorLoad() {
    this.stateFacade.dispatch(SimulatorActions.load());
  }
  dispatchSimulatorLoadSuccess({ model, data }: { model: ISimulatorModel; data: ISimulatorData }) {
    this.stateFacade.dispatch(SimulatorActions.loadSuccess({ model, data }));
  }
  dispatchSimulatorLoadFailure(error: IRemoteError) {
    this.stateFacade.dispatch(SimulatorActions.loadFailure({ error }));
  }

  dispatchSimulatorSubmit() {
    this.stateFacade.dispatch(SimulatorActions.submit());
  }
  dispatchSimulatorSubmitSuccess({ model }: { model: ISimulatorModel }) {
    this.stateFacade.dispatch(SimulatorActions.submitSuccess({ model }));
  }
  dispatchSimulatorSubmitFailure(error: IRemoteError) {
    this.stateFacade.dispatch(SimulatorActions.submitFailure({ error }));
  }
}
